'use client';
import { type Brand } from '@curology/ui-components-web/brand';
import { Typography } from '@curology/ui-components-web/react';
import { Progress } from '@curology/ui-components-web/react/Progress';
import React, { useEffect, useState } from 'react';

import { CheckoutFooter } from '@/components/flow/expressCurologySignUpSkin/components/checkoutFooter';
import { PriceSummaryAccordion } from '@/components/flow/expressCurologySignUpSkin/components/priceSummaryAccordion';
import { type ExpressCurologySignUpSkinPdpSourceConfigType } from '@/components/flow/expressCurologySignUpSkin/types';
import { type TreatmentCategory } from '@/consts/treatmentCategories';
import { useRouter } from '@/hooks/useRouter';
import { getVisit } from '@/services/visitsService/client';
import { type AnalyticsPatient } from '@/types/metrics/analyticsPatient';

import { ApplePayButton } from './components/applePayButton';
import { CreditCardForm } from './components/creditCardForm';
import { PayPalButton } from './components/payPalButton';

export const ClientContainer = ({
  brand,
  bundleConfig,
  giftWithPurchaseImageUrl,
  patient,
  treatmentCategory,
  uuid,
}: {
  brand: Brand;
  bundleConfig: ExpressCurologySignUpSkinPdpSourceConfigType;
  giftWithPurchaseImageUrl?: string;
  patient: AnalyticsPatient;
  treatmentCategory: TreatmentCategory;
  uuid: string;
}) => {
  const [selectedAddOnProductItemIds, setSelectedAddOnProductItemIds] =
    useState<number[]>([]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [payPalProcessing, setPayPalProcessing] = useState(false);

  const router = useRouter();

  const { offerId } = bundleConfig;

  const disableForms = () => setIsSubmitting(true);
  const enableForms = () => setIsSubmitting(false);

  const payPalBlockForms = () => setPayPalProcessing(true);
  const payPalUnblockForms = () => setPayPalProcessing(false);

  useEffect(() => {
    const getCurrentVisit = async () => {
      const visit = await getVisit({ uuid });

      if (visit?.sessionBag?.selectedAddOnProductItemIds) {
        setSelectedAddOnProductItemIds(
          visit.sessionBag.selectedAddOnProductItemIds,
        );
      }

      if (visit?.sessionBag?.hasAddedPaymentMethod) {
        router.refresh();
      }
    };

    getCurrentVisit();
  }, [uuid, router]);

  return (
    <React.Fragment>
      <PriceSummaryAccordion
        bundleConfig={bundleConfig}
        giftWithPurchaseImageUrl={giftWithPurchaseImageUrl}
        selectedAddOnProductItemIds={selectedAddOnProductItemIds}
      />
      <div className="cur-bg-surface-neutral cur-p-4">
        <Typography className="cur-mb-4" weight="bold">
          Create account
        </Typography>
        <Typography>{patient.email}</Typography>
      </div>
      <div className="cur-bg-surface-neutral cur-p-4 cur-relative">
        <Typography className="cur-mb-4" weight="bold">
          Payment method
        </Typography>
        <CreditCardForm
          brand={brand}
          disableForms={disableForms}
          enableForms={enableForms}
          isSubmitting={isSubmitting}
          offerId={offerId}
          uuid={uuid}
        />
        <div className="cur-flex cur-items-center cur-mb-4">
          <div className="cur-border-b cur-border-solid cur-flex-1" />
          <Typography className="cur-mx-4">or</Typography>
          <div className="cur-border-b cur-border-solid cur-flex-1" />
        </div>
        <ApplePayButton
          brand={brand}
          bundleConfig={bundleConfig}
          disableForms={disableForms}
          enableForms={enableForms}
          isSubmitting={isSubmitting}
          selectedAddOnProductItemIds={selectedAddOnProductItemIds}
          treatmentCategory={treatmentCategory}
          uuid={uuid}
        />
        <PayPalButton
          brand={brand}
          bundleConfig={bundleConfig}
          disableForms={disableForms}
          enableForms={enableForms}
          isSubmitting={isSubmitting}
          payPalBlockForms={payPalBlockForms}
          payPalUnblockForms={payPalUnblockForms}
          selectedAddOnProductItemIds={selectedAddOnProductItemIds}
          treatmentCategory={treatmentCategory}
          uuid={uuid}
        />
        {payPalProcessing && (
          <div className="cur-absolute cur-bottom-0 cur-flex cur-items-center cur-justify-center cur-left-0 cur-right-0 cur-top-0 cur-z-[101]">
            <div className="cur-bg-white cur-absolute cur-bottom-0 cur-left-0 cur-opacity-50 cur-right-0 cur-top-0" />
            <Progress progressType="circle" size="lg" state="highlight" />
          </div>
        )}
      </div>
      <div className="cur-bg-surface-neutral cur-p-4">
        <Typography weight="bold">Shipping address</Typography>
      </div>
      <CheckoutFooter
        bundleConfig={bundleConfig}
        selectedAddOnProductItemIds={selectedAddOnProductItemIds}
      />
    </React.Fragment>
  );
};
